import React, { ComponentProps, FC, useState } from 'react';
import { ChevronDownIcon } from '@components/icons/ChevronDown.icon';
import { TextProps } from '@components/Text/Text.types';
import { Value } from '@form/business/form.types';
import classnames from 'classnames';

import './Select.scss';

interface OwnProps {
  label?: string;
  name: string;
  onChange: (value: Value | undefined, event: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Value[];
  value?: Value;
  variant?: TextProps['variant'];
  selectClassName?: string;
}

type Props = Omit<ComponentProps<'select'>, 'onChange' | 'value'> & OwnProps;

function isSelected(selectedValue: Value | undefined) {
  return Boolean(selectedValue && selectedValue.value !== '');
}

export const Select: FC<Props> = ({
  placeholder,
  options,
  label,
  name,
  onChange,
  value,
  selectClassName,
  variant = 'p',
  disabled,
  ...rest
}) => {
  const [selected, setSelected] = useState(isSelected(value));

  const onValueChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = options.find(option => option.value === event.target.value);
    setSelected(isSelected(newValue));
    if (onChange) {
      onChange(newValue, event);
    }
  };

  const selectOptions = options.map(o => (
    <option key={o.value} value={o.value}>
      {o.label}
    </option>
  ));

  if (placeholder) {
    selectOptions.push(
      <option key="placeholder" disabled hidden value={placeholder}>
        {placeholder}
      </option>
    );
  }

  const rootClasses = classnames('plc-select', {
    selected,
  });

  let selectValue: string | undefined;
  if (value) {
    selectValue = value.value;
  } else if (placeholder) {
    selectValue = placeholder;
  }

  return (
    <div className={rootClasses}>
      {label && (
        <label className={variant} htmlFor={name}>
          {label}
        </label>
      )}
      <div className="field">
        <select
          className={classnames(variant, selectClassName)}
          disabled={disabled}
          id={name}
          name={name}
          onChange={onValueChange}
          value={selectValue}
          {...rest}
        >
          {selectOptions}
        </select>
        {!disabled && <ChevronDownIcon height={18} width={18} />}
      </div>
    </div>
  );
};
